import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  CreditCard,
  Gift,
  HeartHandshake,
  Megaphone,
  MessageSquareHeart,
  Phone,
  Play,
  Send,
  ShieldCheck,
  Users,
  VideoOff,
  Wallet,
} from 'lucide-react';
import './customer-b2.css';

const CUSTOMER_B2_BASE = '/customer/b2';
const GIFT_BOX_SRC = '/images/customer-b2/gift-box.webp';
const DEFAULT_BABY_NAME = '손주';
const BANK_LINE = '신한은행 100-035-387966 (주)제제미미';
const BANK_ACCOUNT = '100-035-387966';

type CustomerB2Screen = 'landing' | 'payment' | 'form';
type RequestType = 'gift_direct' | 'phone_consult';

const normalizePathname = (pathname: string): string =>
  pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;

const getSearchParams = (): URLSearchParams => {
  if (typeof window === 'undefined') {
    return new URLSearchParams();
  }

  try {
    return new URLSearchParams(window.location.search);
  } catch {
    return new URLSearchParams();
  }
};

const getPageQuery = (): string | null => getSearchParams().get('page');

const getBabyName = (): string => {
  const params = getSearchParams();
  const raw = params.get('babyname') ?? params.get('babyName') ?? params.get('name');
  const trimmed = raw?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : DEFAULT_BABY_NAME;
};

const resolveScreen = (pathname: string): CustomerB2Screen => {
  const page = getPageQuery();
  const route = page && page.startsWith('customer/b2')
    ? `/${page}`
    : normalizePathname(pathname);

  if (route === `${CUSTOMER_B2_BASE}/payment`) {
    return 'payment';
  }

  if (route === `${CUSTOMER_B2_BASE}/form`) {
    return 'form';
  }

  return 'landing';
};

const navigateTo = (path: string): void => {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
};

const isLikelyPhone = (value: string): boolean => {
  const digits = value.replace(/\D/g, '');
  return /^01\d{8,9}$/.test(digits);
};

type ApplyFormProps = {
  requestType: RequestType;
  title: string;
  subtitle: string;
  submitLabel: string;
  showChildField?: boolean;
  defaultConsult?: boolean;
};

const ApplyForm: React.FC<ApplyFormProps> = ({
  requestType,
  title,
  subtitle,
  submitLabel,
  showChildField = false,
  defaultConsult = false,
}) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [childContact, setChildContact] = useState('');
  const [wantsConsult, setWantsConsult] = useState(defaultConsult);
  const [website, setWebsite] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!toast) {
      return;
    }

    const timer = window.setTimeout(() => {
      setToast(null);
    }, 2200);

    return () => {
      window.clearTimeout(timer);
    };
  }, [toast]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    if (name.trim().length < 2) {
      setToast('성함을 2글자 이상 적어주세요');
      return;
    }

    if (!isLikelyPhone(phone)) {
      setToast('전화번호를 다시 확인해주세요 (예: 010-1234-5678)');
      return;
    }

    if (showChildField && childContact.trim().length === 0) {
      setToast('자녀 휴대폰번호 또는 초대코드를 적어주세요');
      return;
    }

    const submit = async (): Promise<void> => {
      setSubmitting(true);
      try {
        const resp = await fetch('/api/customer-b2-apply', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            requestType,
            name: name.trim(),
            phone: phone.replace(/\D/g, ''),
            childContact: showChildField ? childContact.trim() : '',
            wantsConsult,
            website,
          }),
        });
        if (!resp.ok) {
          throw new Error(`Request failed with status ${resp.status}`);
        }
        setSubmitted(true);
      } catch (error) {
        console.error('Failed to submit customer B2 request', error);
        setToast('신청이 접수되지 않았어요. 잠시 후 다시 시도해주세요');
      } finally {
        setSubmitting(false);
      }
    };

    void submit();
  };

  const copyAccount = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(BANK_ACCOUNT.replace(/-/g, ''));
      setToast('계좌번호를 복사했어요');
    } catch {
      setToast('복사에 실패했어요. 직접 입력해주세요');
    }
  };

  if (submitted) {
    return (
      <main className="customer-b2-page customer-b2-page--done">
        {toast ? <div className="customer-b2-toast" role="status">{toast}</div> : null}
        <span className="customer-b2-done-icon" aria-hidden="true">
          <Check />
        </span>
        <h1 className="customer-b2-done-title">신청이 잘 접수됐어요</h1>
        <p className="customer-b2-done-lead">
          {requestType === 'phone_consult'
            ? '남겨주신 전화번호로 상담원이 연락드릴게요. 평일 오전 10시 ~ 오후 6시 사이에 전화드려요.'
            : '아래 계좌로 입금해주시면 이용권을 바로 보내드려요.'}
        </p>
        <p className="customer-b2-done-note">확인 알림톡을 먼저 보내드릴게요.</p>
        {requestType === 'gift_direct' ? (
          <div className="customer-b2-account">
            <p className="customer-b2-account-label">입금 계좌</p>
            <p className="customer-b2-account-line">{BANK_LINE}</p>
            <p className="customer-b2-account-amount">입금 금액 69,000원 / 1년</p>
            <button type="button" className="customer-b2-copy" onClick={() => { void copyAccount(); }}>
              <Copy aria-hidden="true" />
              계좌번호 복사하기
            </button>
          </div>
        ) : null}
        <div className="customer-b2-dock">
          <div className="customer-b2-dock-inner">
            <button
              type="button"
              className="customer-b2-btn customer-b2-btn-outline customer-b2-btn-single"
              onClick={() => navigateTo(CUSTOMER_B2_BASE)}
            >
              <ChevronLeft aria-hidden="true" />
              처음으로 돌아가기
            </button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="customer-b2-page">
      {toast ? <div className="customer-b2-toast" role="status">{toast}</div> : null}
      <button type="button" className="customer-b2-back" onClick={() => navigateTo(CUSTOMER_B2_BASE)}>
        <ChevronLeft aria-hidden="true" />
        뒤로가기
      </button>
      <h1 className="customer-b2-page-title">{title}</h1>
      <p className="customer-b2-page-lead">{subtitle}</p>
      <form id="apply-form" className="customer-b2-form" onSubmit={handleSubmit}>
        <div className="customer-b2-field">
          <label htmlFor="name">성함</label>
          <input
            id="name"
            className="customer-b2-input"
            maxLength={50}
            placeholder="예) 김영자"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <div className="customer-b2-field">
          <label htmlFor="phone">전화번호</label>
          <input
            id="phone"
            className="customer-b2-input"
            type="tel"
            inputMode="numeric"
            maxLength={20}
            placeholder="예) 010-1234-5678"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
          />
        </div>
        {showChildField ? (
          <div className="customer-b2-field">
            <label htmlFor="child">이용권을 받을 자녀 정보</label>
            <input
              id="child"
              className="customer-b2-input"
              maxLength={100}
              placeholder="자녀 휴대폰번호 또는 앱 초대코드"
              value={childContact}
              onChange={(event) => setChildContact(event.target.value)}
            />
          </div>
        ) : null}
        {/* 스팸 봇 차단용 숨김 필드 — 사람은 채우지 않아요 */}
        <input
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
          value={website}
          onChange={(event) => setWebsite(event.target.value)}
        />
        <div className="customer-b2-check">
          <label className="customer-b2-check-row">
            <button
              type="button"
              role="checkbox"
              aria-checked={wantsConsult}
              className={`customer-b2-checkbox${wantsConsult ? ' is-checked' : ''}`}
              onClick={() => setWantsConsult((current) => !current)}
            >
              {wantsConsult ? <Check aria-hidden="true" /> : null}
            </button>
            <span className="customer-b2-check-label">
              <Phone aria-hidden="true" />
              전화 상담을 받고 싶어요
            </span>
          </label>
          <p className="customer-b2-check-desc">
            평일 오전 10시 ~ 오후 6시 사이 상담원이 전화드려요
          </p>
        </div>
      </form>
      <div className="customer-b2-dock">
        <div className="customer-b2-dock-inner">
          <button
            type="submit"
            form="apply-form"
            className="customer-b2-btn customer-b2-btn-primary customer-b2-btn-single"
            disabled={submitting}
          >
            {submitting ? '신청 중이에요...' : submitLabel}
          </button>
        </div>
      </div>
    </main>
  );
};

const GiftLanding: React.FC<{ babyName: string }> = ({ babyName }) => {
  const mainRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const root = mainRef.current;
    if (!root) {
      return;
    }

    const targets = root.querySelectorAll('.cb2-reveal');
    if (typeof IntersectionObserver === 'undefined') {
      targets.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' },
    );

    targets.forEach((el) => observer.observe(el));
    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <main ref={mainRef} className="customer-b2-main">
      {/* 1. 인사 */}
      <section className="customer-b2-hero">
        <img
          className="customer-b2-icon"
          src={GIFT_BOX_SRC}
          alt="분홍 리본이 달린 선물 상자"
          width={160}
          height={160}
        />
        <h1 className="customer-b2-title">
          {`요즘 할머니 할아버지들은\n${babyName}에게\n이런 선물을 하신대요`}
        </h1>
        <p className="customer-b2-lead">
          {'어떤 선물인지 지금부터\n차근차근 알려드릴게요.'}
        </p>
        <div className="customer-b2-social cb2-reveal">
          <p>
            벌써 <strong className="cb2-highlight">52만 명</strong>의 할머니 할아버지가
            <br />
            쑥쑥찰칵과 함께하고 계세요
          </p>
        </div>
        <div className="customer-b2-scroll-hint" aria-hidden="true">
          <p>아래로 내리면서 천천히 읽어보세요</p>
          <ChevronDown />
        </div>
        <button
          type="button"
          className="customer-b2-skip"
          onClick={() => {
            document.getElementById('cb2-paths')?.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          이미 마음을 정하셨나요? 바로 신청하러 가기
        </button>
      </section>

      {/* 2. 공감 */}
      <section className="customer-b2-empathy" aria-label="불편했던 점">
        <h2 className="customer-b2-section-title cb2-reveal">
          {'혹시 이런 점,\n불편하지 않으셨어요?'}
        </h2>
        <ul className="customer-b2-empathy-list">
          <li className="cb2-reveal">
            <span className="customer-b2-empathy-icon">
              <Megaphone aria-hidden="true" />
            </span>
            <p>{'사진 보다가 광고가 자꾸 나와서\n성가시셨죠?'}</p>
          </li>
          <li className="cb2-reveal">
            <span className="customer-b2-empathy-icon">
              <VideoOff aria-hidden="true" />
            </span>
            <p>{'손주 영상이 중간에 뚝 끊겨서\n아쉬우셨죠?'}</p>
          </li>
        </ul>
        <p className="customer-b2-empathy-answer cb2-reveal">
          {'그래서 준비했어요.\n이용권 하나면 두 가지 모두 해결돼요.'}
        </p>
      </section>

      {/* 3. 딱 3가지 안내 */}
      <section className="customer-b2-steps" aria-label="선물 안내">
        <h2 className="customer-b2-section-title cb2-reveal">
          {'선물하기 전에\n딱 3가지만 알아두세요'}
        </h2>

        <article className="customer-b2-step cb2-reveal">
          <div className="customer-b2-step-head">
            <span className="customer-b2-step-number">1</span>
            <h3>무엇을 사는 건가요?</h3>
          </div>
          <div className="customer-b2-step-body">
            <span className="customer-b2-step-icon">
              <Gift aria-hidden="true" />
            </span>
            <p>
              {'쑥쑥찰칵을 1년 동안 제한 없이 쓰는\n「1년 이용권」이에요.'}
            </p>
          </div>
        </article>

        <div className="customer-b2-step-connector" aria-hidden="true" />

        <article className="customer-b2-step cb2-reveal">
          <div className="customer-b2-step-head">
            <span className="customer-b2-step-number">2</span>
            <h3>누구에게 좋은가요?</h3>
          </div>
          <div className="customer-b2-step-body">
            <span className="customer-b2-step-icon">
              <Users aria-hidden="true" />
            </span>
            <p>{'자녀 가족에게 선물하시면\n할머니 할아버지까지\n온 가족이 함께 누려요.'}</p>
          </div>
          <div className="customer-b2-benefit-grid">
            <div className="customer-b2-benefit">
              <span className="customer-b2-benefit-icon">
                <Users aria-hidden="true" />
              </span>
              <h4>{'온 가족이\n광고 없이 편하게'}</h4>
            </div>
            <div className="customer-b2-benefit">
              <span className="customer-b2-benefit-icon">
                <Play aria-hidden="true" fill="currentColor" />
              </span>
              <h4>{'영상을\n끊김 없이 길게'}</h4>
            </div>
          </div>
        </article>

        <div className="customer-b2-step-connector" aria-hidden="true" />

        <article className="customer-b2-step cb2-reveal">
          <div className="customer-b2-step-head">
            <span className="customer-b2-step-number">3</span>
            <h3>가격은 얼마인가요?</h3>
          </div>
          <div className="customer-b2-step-body">
            <span className="customer-b2-step-icon">
              <Wallet aria-hidden="true" />
            </span>
            <p>{'하루 200원꼴이에요.\n1년에 딱 한 번만 결제하면 돼요.'}</p>
          </div>
          <div className="customer-b2-price">
            <span className="customer-b2-price-badge">지금 선물하면 30% 할인</span>
            <p className="customer-b2-price-label">1년 이용권 · 1년에 딱 한 번 결제</p>
            <p className="customer-b2-price-monthly">
              69,000
              <span>원</span>
            </p>
            <p className="customer-b2-price-year">
              <s>정가 99,000원</s>
              <em>→ 30% 할인</em>
            </p>
            <div className="customer-b2-price-divider" />
            <p className="customer-b2-price-note">한 달로 치면 5,900원꼴이에요</p>
            <p className="customer-b2-price-safe">
              매달 빠져나가는 게 아니에요.
              <br />
              1년에 딱 한 번이에요.
            </p>
          </div>
        </article>
      </section>

      {/* 4. 신청 절차 */}
      <section id="cb2-paths" className="customer-b2-flow-section" aria-label="신청 절차">
        <h2 className="customer-b2-section-title cb2-reveal">
          {'신청 방법은\n두 가지가 있어요'}
        </h2>
        <p className="customer-b2-section-lead cb2-reveal">
          {'편하신 쪽으로 진행하시면 돼요.'}
        </p>

        <article className="customer-b2-path customer-b2-path--primary cb2-reveal">
          <div className="customer-b2-path-head">
            <span className="customer-b2-path-icon">
              <Gift aria-hidden="true" />
            </span>
            <h3>바로 시작하실 분</h3>
          </div>
          <ol className="customer-b2-flow">
            <li>
              <span className="customer-b2-flow-icon">
                <Send aria-hidden="true" />
              </span>
              <div>
                <h4>성함과 전화번호를 남겨주세요</h4>
                <p>1분이면 충분해요</p>
              </div>
            </li>
            <li>
              <span className="customer-b2-flow-icon">
                <CreditCard aria-hidden="true" />
              </span>
              <div>
                <h4>이어지는 화면에서 바로 결제하세요</h4>
                <p>카드로 간편하고 안전하게 결제돼요</p>
              </div>
            </li>
            <li>
              <span className="customer-b2-flow-icon customer-b2-flow-icon--last">
                <Gift aria-hidden="true" />
              </span>
              <div>
                <h4>{`${babyName}네 가족에게 이용권이 선물돼요`}</h4>
                <p>결제가 끝나면 온 가족이 바로 쓸 수 있어요</p>
              </div>
            </li>
          </ol>
          <button
            type="button"
            className="customer-b2-btn customer-b2-btn-primary customer-b2-btn-stack customer-b2-path-cta"
            onClick={() => navigateTo(`${CUSTOMER_B2_BASE}/payment`)}
          >
            <span className="customer-b2-btn-label">
              지금 바로 시작하기
              <ChevronRight aria-hidden="true" />
            </span>
            <small>(바로 결제하기)</small>
          </button>
        </article>

        <p className="customer-b2-path-or cb2-reveal" aria-hidden="true">또는</p>

        <article className="customer-b2-path cb2-reveal">
          <div className="customer-b2-path-head">
            <span className="customer-b2-path-icon">
              <Phone aria-hidden="true" />
            </span>
            <h3>먼저 물어보고 싶으신 분</h3>
          </div>
          <ol className="customer-b2-flow">
            <li>
              <span className="customer-b2-flow-icon">
                <Send aria-hidden="true" />
              </span>
              <div>
                <h4>성함과 전화번호만 남겨주세요</h4>
                <p>다른 건 적지 않으셔도 돼요</p>
              </div>
            </li>
            <li>
              <span className="customer-b2-flow-icon">
                <Phone aria-hidden="true" />
              </span>
              <div>
                <h4>상담원이 전화를 드려요</h4>
                <p>평일 오전 10시 ~ 오후 6시 사이에 드려요</p>
              </div>
            </li>
            <li>
              <span className="customer-b2-flow-icon customer-b2-flow-icon--last">
                <MessageSquareHeart aria-hidden="true" />
              </span>
              <div>
                <h4>궁금한 점을 다 여쭤보고 정하세요</h4>
                <p>통화 후에 천천히 결정하셔도 돼요</p>
              </div>
            </li>
          </ol>
          <button
            type="button"
            className="customer-b2-btn customer-b2-btn-outline customer-b2-btn-stack customer-b2-path-cta"
            onClick={() => navigateTo(`${CUSTOMER_B2_BASE}/form`)}
          >
            <span className="customer-b2-btn-label">
              <Phone aria-hidden="true" />
              전화로 물어보고 시작하기
              <ChevronRight aria-hidden="true" />
            </span>
            <small>(상담원이 친절히 알려드려요)</small>
          </button>
        </article>
      </section>

      {/* 5. 안심 약속 */}
      <section className="customer-b2-promise" aria-label="안심 약속">
        <h2 className="customer-b2-section-title cb2-reveal">
          {'세 가지를 약속드려요'}
        </h2>
        <ul className="customer-b2-promise-list">
          <li className="cb2-reveal">
            <span className="customer-b2-promise-icon">
              <ShieldCheck aria-hidden="true" />
            </span>
            <p>{'남겨주신 성함과 전화번호는\n선물 안내에만 쓰고,\n다른 곳에는 절대 쓰지 않아요.'}</p>
          </li>
          <li className="cb2-reveal">
            <span className="customer-b2-promise-icon">
              <Phone aria-hidden="true" />
            </span>
            <p>{'어려운 게 있으면 상담원이\n전화로 하나하나 도와드려요.'}</p>
          </li>
          <li className="cb2-reveal">
            <span className="customer-b2-promise-icon">
              <HeartHandshake aria-hidden="true" />
            </span>
            <p>{'서두르지 않으셔도 괜찮아요.\n천천히 생각하고 정하셔도 돼요.'}</p>
          </li>
        </ul>
      </section>

    </main>
  );
};

export const CustomerB2View: React.FC = () => {
  const [pathname, setPathname] = useState(() => (
    typeof window === 'undefined' ? CUSTOMER_B2_BASE : window.location.pathname
  ));
  const screen = useMemo(() => resolveScreen(pathname), [pathname]);
  const babyName = useMemo(() => getBabyName(), [pathname]);

  useEffect(() => {
    const titles: Record<CustomerB2Screen, string> = {
      landing: '쑥쑥찰칵 1년 이용권 선물 — 할머니 할아버지를 위한 안내',
      payment: '이용권 선물하기 — 1년 69,000원',
      form: '전화 상담 신청 — 쑥쑥찰칵 이용권 선물',
    };
    document.title = titles[screen];
  }, [screen]);

  useEffect(() => {
    const syncPathname = () => {
      setPathname(window.location.pathname);
    };

    window.addEventListener('popstate', syncPathname);
    return () => {
      window.removeEventListener('popstate', syncPathname);
    };
  }, []);

  return (
    <div className="customer-b2">
      {screen === 'payment' ? (
        <ApplyForm
          requestType="gift_direct"
          title="잘 생각하셨어요!"
          subtitle="1년 69,000원 (월 5,900원). 신청하시면 입금 계좌를 안내드려요"
          submitLabel="이용권 선물 신청하기"
          showChildField
        />
      ) : null}
      {screen === 'form' ? (
        <ApplyForm
          requestType="phone_consult"
          title="전화로 편하게 물어보세요"
          subtitle="성함과 전화번호만 남겨주시면 상담원이 전화로 알려드려요"
          submitLabel="전화 상담 신청하기"
          defaultConsult
        />
      ) : null}
      {screen === 'landing' ? <GiftLanding babyName={babyName} /> : null}
    </div>
  );
};
